import type { EngagementProviderBootInput } from "@/provider/ProviderTypes";
import type { WindowWithCaptainUpTestFlags } from "@/integrations/captainup/CaptainUpTestFlags";
import { debugError, debugLog, debugWarn } from "@/utils/debug";
import {
	CaptainUpAuthInitError,
	fetchCaptainUpAuthOrThrow,
} from "@/integrations/captainup/CaptainUpAuthService";
import type { CaptainUpAuthMode } from "@/integrations/captainup/CaptainUpBoot";
import { CaptainUpProvider } from "@/providers/captainup/CaptainUpProvider";

type CaptainUpBootFailureStage = "auth_init" | "sdk_init";

type CaptainUpBootLogContext = {
	featureProvider: "captainup";
	stage: CaptainUpBootFailureStage;
	reason:
		| "timeout"
		| "bad_request"
		| "not_found"
		| "server_error"
		| "network_error"
		| "invalid_response"
		| "sdk_init_failure";
	gameName: string;
	lang: string;
	operator: string;
	sessionKey: string;
	status?: number;
	message: string;
	stack?: string;
};

function isDemoModeFromUrl(): boolean {
	try {
		const url = new URL(window.location.href);
		return url.searchParams.get("demo") === "1";
	} catch {
		return false;
	}
}

function resolveCaptainUpInitEndpoint(serverUrl: string): string {
	const normalizedServerUrl = String(serverUrl ?? "").trim();

	if (!normalizedServerUrl) {
		throw new Error("[CaptainUpBootWorker] missing serverUrl in provider boot input");
	}

	try {
		const baseUrl = new URL(normalizedServerUrl);

		if (baseUrl.protocol !== "http:" && baseUrl.protocol !== "https:") {
			throw new Error("[CaptainUpBootWorker] unsupported serverUrl protocol");
		}

		return new URL("/engagement/init", baseUrl).toString();
	} catch (error) {
		const message = error instanceof Error ? error.message : String(error);

		throw new Error(
			`[CaptainUpBootWorker] invalid serverUrl in provider boot input: ${message}`
		);
	}
}

function consumeCaptainUpAuthTimeoutCheat(): boolean {
	if (typeof window === "undefined") {
		return false;
	}

	const targetWindow = window as WindowWithCaptainUpTestFlags;
	const shouldTimeout =
		targetWindow.__EFW_TEST_CAPTAINUP_AUTH_TIMEOUT__ === true;

	if (shouldTimeout) {
		targetWindow.__EFW_TEST_CAPTAINUP_AUTH_TIMEOUT__ = false;
	}

	return shouldTimeout;
}

function consumeCaptainUpAuthBadRequestCheat(): boolean {
	if (typeof window === "undefined") {
		return false;
	}

	const targetWindow = window as WindowWithCaptainUpTestFlags;
	const shouldFail =
		targetWindow.__EFW_TEST_CAPTAINUP_AUTH_BAD_REQUEST__ === true;

	if (shouldFail) {
		targetWindow.__EFW_TEST_CAPTAINUP_AUTH_BAD_REQUEST__ = false;
	}

	return shouldFail;
}

function consumeCaptainUpAuthNotFoundCheat(): boolean {
	if (typeof window === "undefined") {
		return false;
	}

	const targetWindow = window as WindowWithCaptainUpTestFlags;
	const shouldFail =
		targetWindow.__EFW_TEST_CAPTAINUP_AUTH_NOT_FOUND__ === true;

	if (shouldFail) {
		targetWindow.__EFW_TEST_CAPTAINUP_AUTH_NOT_FOUND__ = false;
	}

	return shouldFail;
}

function consumeCaptainUpAuthServerErrorCheat(): boolean {
	if (typeof window === "undefined") {
		return false;
	}

	const targetWindow = window as WindowWithCaptainUpTestFlags;
	const shouldFail =
		targetWindow.__EFW_TEST_CAPTAINUP_AUTH_SERVER_ERROR__ === true;

	if (shouldFail) {
		targetWindow.__EFW_TEST_CAPTAINUP_AUTH_SERVER_ERROR__ = false;
	}

	return shouldFail;
}

function consumeCaptainUpSdkInitFailureCheat(): boolean {
	if (typeof window === "undefined") {
		return false;
	}

	const targetWindow = window as WindowWithCaptainUpTestFlags;
	const shouldFail =
		targetWindow.__EFW_TEST_CAPTAINUP_SDK_INIT_FAILURE__ === true;

	if (shouldFail) {
		targetWindow.__EFW_TEST_CAPTAINUP_SDK_INIT_FAILURE__ = false;
	}

	return shouldFail;
}

function buildCaptainUpBootBaseLogContext(
	bootInput: EngagementProviderBootInput
): Pick<
	CaptainUpBootLogContext,
	"featureProvider" | "gameName" | "lang" | "operator" | "sessionKey"
> {
	return {
		featureProvider: "captainup",
		gameName: bootInput.gameName,
		lang: bootInput.lang,
		operator: bootInput.operator,
		sessionKey: bootInput.sessionKey,
	};
}

function buildCaptainUpAuthFailureLogContext(
	bootInput: EngagementProviderBootInput,
	error: CaptainUpAuthInitError
): CaptainUpBootLogContext {
	const reason: CaptainUpBootLogContext["reason"] =
		error.kind === "TIMEOUT"
			? "timeout"
			: error.kind === "BAD_REQUEST"
				? "bad_request"
				: error.kind === "NOT_FOUND"
					? "not_found"
					: error.kind === "SERVER_ERROR"
						? "server_error"
						: error.kind === "NETWORK_ERROR"
							? "network_error"
							: "invalid_response";

	return {
		stage: "auth_init",
		reason,
		...buildCaptainUpBootBaseLogContext(bootInput),
		status: error.status,
		message: error.message,
	};
}

function buildCaptainUpSdkFailureLogContext(
	bootInput: EngagementProviderBootInput,
	error: Error
): CaptainUpBootLogContext {
	return {
		stage: "sdk_init",
		reason: "sdk_init_failure",
		...buildCaptainUpBootBaseLogContext(bootInput),
		message: error.message,
		stack: error.stack,
	};
}

function logCaptainUpBootFailure(
	bootInput: EngagementProviderBootInput,
	error: unknown
): void {
	if (error instanceof CaptainUpAuthInitError) {
		const context = buildCaptainUpAuthFailureLogContext(bootInput, error);

		if (error.kind === "BAD_REQUEST" || error.kind === "NOT_FOUND") {
			debugWarn("[EF CaptainUpBootWorker] graceful degradation", context);
			return;
		}

		debugError("[EF CaptainUpBootWorker] graceful degradation", context);
		return;
	}

	const normalizedError =
		error instanceof Error ? error : new Error(String(error));
	const context = buildCaptainUpSdkFailureLogContext(
		bootInput,
		normalizedError
	);

	debugError("[EF CaptainUpBootWorker] graceful degradation", context);
}

async function resolveCaptainUpAuthMode(
	bootInput: EngagementProviderBootInput
): Promise<CaptainUpAuthMode> {
	const demo = isDemoModeFromUrl();
	const endpointUrl = resolveCaptainUpInitEndpoint(bootInput.serverUrl);

	debugLog("[EF CaptainUpBootWorker] auth resolution starting", {
		featureProvider: "captainup",
		demo,
		endpointUrl,
		gameName: bootInput.gameName,
		lang: bootInput.lang,
		sessionKeyPresent: !!bootInput.sessionKey,
	});

	if (consumeCaptainUpAuthTimeoutCheat()) {
		throw new CaptainUpAuthInitError(
			"TIMEOUT",
			"[CaptainUpAuthService] auth request timed out"
		);
	}

	if (consumeCaptainUpAuthBadRequestCheat()) {
		throw new CaptainUpAuthInitError(
			"BAD_REQUEST",
			"[CaptainUpAuthService] auth request failed: 400 Bad Request",
			400
		);
	}

	if (consumeCaptainUpAuthNotFoundCheat()) {
		throw new CaptainUpAuthInitError(
			"NOT_FOUND",
			"[CaptainUpAuthService] auth request failed: 404 Not Found",
			404
		);
	}

	if (consumeCaptainUpAuthServerErrorCheat()) {
		throw new CaptainUpAuthInitError(
			"SERVER_ERROR",
			"[CaptainUpAuthService] auth request failed: 503 Service Unavailable",
			503
		);
	}

	if (demo) {
		return { mode: "demo" };
	}

	const response = await fetchCaptainUpAuthOrThrow(bootInput.sessionKey, {
		mode: "network",
		endpointUrl,
	});

	return { mode: "secure", auth: response.captainUp };
}

/**
 * This file is intentionally the single CaptainUp boot worker boundary.
 * It knows how to authenticate and construct a CaptainUp provider.
 * It must not know anything about generations, supervision, runtime stores,
 * React state, or retries.
 */
export async function bootCaptainUpProviderOrThrow(
	bootInput: EngagementProviderBootInput
): Promise<CaptainUpProvider> {
	try {
		debugLog("[EF CaptainUpBootWorker] boot starting", {
			featureProvider: "captainup",
			gameName: bootInput.gameName,
			lang: bootInput.lang,
			sessionKeyPresent: !!bootInput.sessionKey,
		});

		const authMode = await resolveCaptainUpAuthMode(bootInput);

		if (consumeCaptainUpSdkInitFailureCheat()) {
			throw new Error("[CaptainUpBootWorker] cheat-triggered SDK init failure");
		}

		const provider = await CaptainUpProvider.bootOrThrow(
			authMode,
			{
				fonts: {
					headerFontName: "Montserrat",
					contentFontName: "Roboto",
				},
				layout: "popup9",
			},
			"challenges, tournament",
			{
				gameName: bootInput.gameName,
				lang: bootInput.lang,
			}
		);

		debugLog("[EF CaptainUpBootWorker] boot complete", {
			featureProvider: "captainup",
			hasProvider: true,
		});

		return provider;
	} catch (error) {
		logCaptainUpBootFailure(bootInput, error);
		throw error;
	}
}